import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowDown, ArrowUp, ArrowUUpLeft, CheckCircle, LockOpen, DotsSixVertical, EnvelopeSimple, Eye, FileDoc, FilePdf, Plus, PushPin, Trash, UserPlus,
  Warning,
} from "@phosphor-icons/react";
import { Diktat } from "../components/Diktat";
import { Zurueckholen } from "../components/Zurueckholen";
import { Einwaende } from "../components/Einwaende";
import { ErgaenzungDialog, ErgaenzungKnopf, ErgaenzungListe } from "../components/Ergaenzung";
import { NeuerMangel, useLeistungen } from "./projekt/MaengelReiter";
import {
  DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, pointerWithin, rectIntersection,
  useDraggable, useDroppable, useSensor, useSensors, type CollisionDetection, type DragEndEvent,
} from "@dnd-kit/core";
import { ApiFehler, aender, api, lies, schick, weg } from "../api/client";
import type { Besprechung, Beteiligter, Eintrag, PunktArt, PunktStatus, Schreiben, Teilnahme } from "../api/types";
import { Blur, Dialog, Meldung } from "../components/Bausteine";
import { FotoLeiste } from "../components/FotoLeiste";
import { VersandDialog } from "../components/Versand";
import { usePfad } from "../lib/pfad";
import { useProjekt } from "./projekt/ProjektSeite";
import { useIch } from "../App";
import { BESPRECHUNG_ART, PUNKT_ART, datum, ortszeitEingabe } from "../lib/format";
import { useBeteiligte } from "./projekt/BeteiligteReiter";
import { Auswahl } from "../components/Auswahl";

/**
 * Der Protokoll-Editor (DECISIONS E7, E16). Mitschreiben in der Besprechung:
 * jedes Feld speichert beim Verlassen, ein Punkt ist ein Tipp auf „+“ entfernt,
 * Art und Status sind Knöpfe statt Auswahllisten — auf dem Tablet ein Tipp.
 */
/** Eine Schlange je Protokoll fuer ALLE Aenderungen daran und die Freigabe (E16). */
const protokollScope = (id: string) => `protokoll-${id}`;

export function BesprechungSeite() {
  const { besprechungId = "" } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const schluessel = ["besprechung", besprechungId];
  const b = useQuery({ queryKey: schluessel, queryFn: () => lies<Besprechung>(`/besprechungen/${besprechungId}`) });
  const beteiligte = useBeteiligte(b.data?.projekt_id);
  const projekt = useProjekt(b.data?.projekt_id).data;
  usePfad(projekt && b.data ? [
    { titel: projekt.name, nach: `/p/${projekt.kurz}/besprechungen` },
    { titel: "Besprechungen", nach: `/p/${projekt.kurz}/besprechungen` },
    { titel: `Nr. ${b.data.nummer}` },
  ] : []);
  const [stand, setStand] = useState<{ text: string; fehler?: boolean } | null>(null);
  const [freigeben, setFreigeben] = useState(false);
  const [versand, setVersand] = useState(false);
  // „Als Mangel erfassen" (SPEC §5.7): der Punkt, aus dem gerade ein Mangel wird.
  const [mangelAus, setMangelAus] = useState<Eintrag | null>(null);
  // Ergänzung nach der Freigabe (SPEC §4.12): Eintrag-Kennung, "" = zum Protokoll allgemein.
  const [ergaenzung, setErgaenzung] = useState<string | null>(null);
  const ergaenzt = (neu: Besprechung) => qc.setQueryData<Besprechung>(schluessel, (alt) => ({ ...neu, meine_rolle: alt?.meine_rolle ?? neu.meine_rolle }));
  const leistungen = useLeistungen(mangelAus ? b.data?.projekt_id : undefined);
  // Das abgelegte Original (E10): ein freigegebenes Protokoll wird nicht neu gedruckt,
  // sondern so ausgeliefert, wie es rausging.
  const ausgang = useQuery({
    queryKey: ["schreiben", b.data?.projekt_id],
    queryFn: () => lies<Schreiben[]>(`/projekte/${b.data!.projekt_id}/schreiben`),
    enabled: b.data?.status === "freigegeben",
  });
  // Je Fassung ein PDF (Wanderung 017): gilt nur, was seit DIESER Freigabe abgelegt ist —
  // nach einer Wiederöffnung läge sonst das alte PDF als „das“ Protokoll da.
  const seit = b.data?.freigegeben_am ? new Date(b.data.freigegeben_am).getTime() : 0;
  const abgelegt = ausgang.data?.find((s) => s.besprechung_id === besprechungId && new Date(s.erzeugt_am).getTime() >= seit - 1000);
  const ich = useIch().data;
  const [oeffnen, setOeffnen] = useState(false);

  const neuLaden = () => qc.invalidateQueries({ queryKey: schluessel });
  const gemeldet = (f: unknown) => {
    setStand({ text: f instanceof ApiFehler ? f.message : "Nicht gespeichert.", fehler: true });
    if (f instanceof ApiFehler && f.status === 409) neuLaden();
  };
  const gespeichert = () => setStand({ text: "gespeichert" });

  const kopf = useMutation({
    scope: { id: protokollScope(besprechungId) },
    mutationFn: (r: Partial<Besprechung>) => aender<Besprechung>(`/besprechungen/${besprechungId}`, r),
    // NUR die Kopffelder uebernehmen: die Antwort ist ein ganzes Protokoll, und eine
    // Eintragsaenderung, die inzwischen durch ist, truege sonst wieder ihren alten Stand
    // (`geaendert_am`) — die naechste Aenderung daran bekaeme ein falsches 409.
    onSuccess: async (neu) => {
      await qc.cancelQueries({ queryKey: schluessel });
      qc.setQueryData<Besprechung>(schluessel, (alt) => alt && { ...neu, themen: alt.themen, teilnahme: alt.teilnahme, meine_rolle: alt.meine_rolle });
      gespeichert();
    },
    onError: gemeldet,
  });
  // Hintereinander, nicht nebeneinander (`scope`): Text verlassen und sofort „erledigt“
  // tippen sind zwei Änderungen am selben Eintrag. Liefen sie gleichzeitig, trüge die
  // zweite den alten Stand und bekäme ein 409 (E16). Den Stand liest erst die laufende
  // Änderung aus dem Speicher — dann ist der der ersten schon da.
  const eintrag = useMutation({
    scope: { id: protokollScope(besprechungId) },
    mutationFn: ({ e, r }: { e: Eintrag; r: Record<string, unknown> }) => {
      const aktuell = qc.getQueryData<Besprechung>(schluessel)?.themen
        .flatMap((t) => t.eintraege).find((x) => x.id === e.id);
      return aender<Eintrag>(`/eintraege/${e.id}`, { ...r, stand: (aktuell ?? e).geaendert_am });
    },
    onSuccess: async (neu) => {
      // Ein Nachladen, das noch unterwegs ist, ueberschriebe den neuen Stand gleich wieder.
      await qc.cancelQueries({ queryKey: schluessel });
      qc.setQueryData<Besprechung>(schluessel, (alt) => alt && {
        ...alt,
        themen: alt.themen.map((t) => ({ ...t, eintraege: t.eintraege.map((x) => (x.id === neu.id ? neu : x)) })),
      });
      gespeichert();
    },
    onError: gemeldet,
  });
  // Drag-and-Drop (SPEC §4.7). Gezogen wird nur am Griff: so bleiben Textfelder und
  // Knoepfe im Punkt normal bedienbar. Maus ab 6 px Weg; Finger nach kurzem Halten, sonst
  // waere jedes Wischen zum Scrollen ein Ziehen. Tastatur: Griff fokussieren, Leertaste.
  const sensoren = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const [gezogen, setGezogen] = useState<Eintrag | null>(null);
  // Rueckgaengig (SPEC §4.8): nach Verschieben, Status, Art und Rot zehn Sekunden lang
  // ein Hinweis mit Knopf. Ein Fehlgriff auf dem Tablet ist sonst Suchen und Nachbauen.
  const [rueck, setRueck] = useState<{ text: string; tun: () => void } | null>(null);
  useEffect(() => {
    if (!rueck) return;
    const t = setTimeout(() => setRueck(null), 10_000);
    return () => clearTimeout(t);
  }, [rueck]);
  const verschieben = useMutation({
    scope: { id: protokollScope(besprechungId) },
    mutationFn: ({ e, thema_id, vor, lfd }: { e: Eintrag; thema_id: string; vor: string | null; lfd?: number }) =>
      schick<Besprechung>(`/eintraege/${e.id}/verschieben`, { thema_id, vor, lfd }),
    onMutate: async ({ e, thema_id, vor }) => {
      // Sofort zeigen, wo der Punkt landet; die Antwort bringt danach die neue Nummer.
      await qc.cancelQueries({ queryKey: schluessel });
      qc.setQueryData<Besprechung>(schluessel, (alt) => alt && umsortiert(alt, e, thema_id, vor));
    },
    onSuccess: (neu) => {
      qc.setQueryData<Besprechung>(schluessel, (alt) => ({ ...neu, meine_rolle: alt?.meine_rolle ?? neu.meine_rolle }));
      gespeichert();
    },
    onError: (f) => { gemeldet(f); neuLaden(); },
  });
  const abgelegt_ = (ev: DragEndEvent) => {
    setGezogen(null);
    const ziel = ev.over?.data.current as { thema_id: string; vor: string | null } | undefined;
    const e = ev.active.data.current?.e as Eintrag | undefined;
    if (!ziel || !e || ziel.vor === e.id) return;
    // Wo er vorher stand, fuer Rueckgaengig: Thema, Nachfolger, alte Nummer.
    const vorher = b.data?.themen.find((t) => t.eintraege.some((x) => x.id === e.id));
    const i = vorher?.eintraege.findIndex((x) => x.id === e.id) ?? -1;
    const nach = vorher?.eintraege[i + 1]?.id ?? null;
    const altLfd = Number(e.nummer.split(".")[1]);
    verschieben.mutate({ e, thema_id: ziel.thema_id, vor: ziel.vor }, {
      onSuccess: () => vorher && setRueck({
        text: `${e.nummer} verschoben`,
        tun: () => verschieben.mutate({ e, thema_id: vorher.id, vor: nach, lfd: altLfd }),
      }),
    });
  };
  /** Aenderungen mit Rueckgaengig: Status, Art, Rot. Text hat es nicht — dafuer ist das
   *  Feld selbst da (Strg+Z), und ein Rueckgaengig ueber Sitzungen hinweg waere zu viel. */
  const aendernMitRueck = (e: Eintrag, r: Record<string, unknown>) => {
    const rueckbar = ["status", "art", "wichtig"].filter((k) => k in r);
    const alt = Object.fromEntries(rueckbar.map((k) => [k, (e as unknown as Record<string, unknown>)[k]]));
    eintrag.mutate({ e, r }, {
      onSuccess: () => rueckbar.length && setRueck({
        text: `${e.nummer}: ${rueckbar.map((k) => ({ status: "Status", art: "Art", wichtig: "Hervorhebung" })[k]).join(", ")} geändert`,
        tun: () => eintrag.mutate({ e, r: alt }),
      }),
    });
  };
  const gliederung = useMutation({
    scope: { id: protokollScope(besprechungId) },
    mutationFn: ({ methode, pfad, rumpf }: { methode: "POST" | "PATCH" | "PUT" | "DELETE"; pfad: string; rumpf?: unknown }) =>
      api(methode, `/projekte/${b.data!.projekt_id}/themen${pfad}`, rumpf),
    onSuccess: () => { neuLaden(); gespeichert(); },
    onError: gemeldet,
  });
  const verschiebeThema = (themaId: string, richtung: -1 | 1) => {
    const ids = b.data!.themen.map((t) => t.id);
    const i = ids.indexOf(themaId);
    const j = i + richtung;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    gliederung.mutate({ methode: "PUT", pfad: "/folge", rumpf: { folge: ids } });
  };
  const [neuerAbschnitt, setNeuerAbschnitt] = useState("");
  const [zurueck, setZurueck] = useState(false);
  const punktNeu = useMutation({
    scope: { id: protokollScope(besprechungId) },
    mutationFn: (themaId: string) => schick<Eintrag>(`/besprechungen/${besprechungId}/punkte`, { thema_id: themaId, art: "I" }),
    onSuccess: () => { neuLaden(); gespeichert(); },
    onError: gemeldet,
  });
  const punktWeg = useMutation({
    scope: { id: protokollScope(besprechungId) },
    mutationFn: (e: Eintrag) => weg(`/eintraege/${e.id}`),
    onSuccess: neuLaden, onError: gemeldet,
  });
  const nachholen = useMutation({
    mutationFn: () => schick(`/besprechungen/${besprechungId}/schreiben`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["schreiben", b.data?.projekt_id] }); gespeichert(); },
    onError: gemeldet,
  });
  const verwerfen = useMutation({
    mutationFn: () => weg(`/besprechungen/${besprechungId}`),
    onSuccess: () => {
      // Sonst zeigt die Liste den verworfenen Entwurf aus dem Zwischenspeicher weiter an.
      qc.invalidateQueries({ queryKey: ["besprechungen", b.data!.projekt_id] });
      nav(`/p/${b.data!.projekt_id}/besprechungen`);
    },
    onError: gemeldet,
  });

  if (b.error) return <main className="seite"><Meldung fehler={b.error} /></main>;
  if (!b.data) return null;
  const p = b.data;
  const offen = p.status === "entwurf" && p.meine_rolle !== "lesen";

  return (
    <main className="seite">
      <div className="seite-kopf">
        <div style={{ minWidth: 0 }}>
          <h1>{p.titel || BESPRECHUNG_ART[p.art]} – {p.nummer}</h1>
          <div className="dim">{datum(p.datum)}{p.ort && ` · ${p.ort}`}
            {p.importiert_aus && ` · übernommen aus ${p.importiert_aus}`}</div>
        </div>
        <div className="luft" />
        {p.status === "entwurf" ? <span className="chip warn">Entwurf</span> : <span className="chip gut">freigegeben {datum(p.freigegeben_am)}</span>}
      </div>

      {offen && (
        <section className="karte">
          <div className="raster">
            <label className="feld"><span>Datum</span>
              <input type="date" className="eingabe" defaultValue={p.datum} onBlur={(e) => e.target.value !== p.datum && kopf.mutate({ datum: e.target.value })} /></label>
            <label className="feld"><span>Beginn</span>
              <input type="time" className="eingabe" defaultValue={p.beginn?.slice(0, 5) ?? ""} onBlur={(e) => kopf.mutate({ beginn: e.target.value || null })} /></label>
            <label className="feld"><span>Ende</span>
              <input type="time" className="eingabe" defaultValue={p.ende?.slice(0, 5) ?? ""} onBlur={(e) => kopf.mutate({ ende: e.target.value || null })} /></label>
            <label className="feld"><span>Ort</span><Blur wert={p.ort} speichern={(v) => kopf.mutate({ ort: v })} /></label>
            <label className="feld breit"><span>Protokolltitel</span>
              <Blur wert={p.titel} placeholder="z. B. Baubesprechung Außenanlagen" speichern={(v) => kopf.mutate({ titel: v })} /></label>
            <label className="feld breit"><span>Vorbemerkung</span>
              <Blur mehrzeilig wert={p.vorbemerkung} placeholder="optional, steht im Protokoll vor den Punkten" speichern={(v) => kopf.mutate({ vorbemerkung: v })} /></label>
          </div>
        </section>
      )}

      {p.status === "entwurf" && !!p.wiedereroeffnungen?.length && (() => {
        const w = p.wiedereroeffnungen[p.wiedereroeffnungen.length - 1]!;
        return (
          <div className="hinweise"><div className="hinweis">
            <b>Wieder geöffnet</b> am {datum(w.am)}{w.von_name && ` von ${w.von_name}`} – Grund: {w.grund}.
            Fassung {w.fassung}{w.freigegeben_am && ` vom ${datum(w.freigegeben_am)}`} bleibt im Ausgang; die nächste Freigabe
            legt Fassung {p.wiedereroeffnungen.length + 1} ab und vermerkt im Kopf, dass sie die frühere ersetzt.
          </div></div>
        );
      })()}

      <Teilnehmende b={p} offen={offen} beteiligte={beteiligte.data ?? []} neuLaden={neuLaden} gemeldet={gemeldet} />

      <DndContext sensors={sensoren} collisionDetection={zielErkennen}
                  onDragStart={(ev) => setGezogen((ev.active.data.current?.e as Eintrag) ?? null)}
                  onDragEnd={abgelegt_} onDragCancel={() => setGezogen(null)}>
      {p.themen.map((t, ti) => (
        <section key={t.id} className="thema">
          <div className="thema-kopf">
            {offen ? (
              <>
                <h2 className="thema-nr">{String(t.nummer).padStart(2, "0")}</h2>
                <Blur className="thema-titel" wert={t.titel} aria-label={`Titel Abschnitt ${t.nummer}`}
                      speichern={(v) => v.trim() && gliederung.mutate({ methode: "PATCH", pfad: `/${t.id}`, rumpf: { titel: v } })} />
                <button className="knopf leise symbol" aria-label="Abschnitt nach oben" disabled={ti === 0}
                        onClick={() => verschiebeThema(t.id, -1)}><ArrowUp size={15} /></button>
                <button className="knopf leise symbol" aria-label="Abschnitt nach unten" disabled={ti === p.themen.length - 1}
                        onClick={() => verschiebeThema(t.id, 1)}><ArrowDown size={15} /></button>
                {t.eintraege.length === 0 && p.meine_rolle === "leitung" && (
                  <button className="knopf leise symbol gefahr" aria-label="Leeren Abschnitt entfernen"
                          onClick={() => confirm(`Abschnitt „${t.titel}“ entfernen?`) && gliederung.mutate({ methode: "DELETE", pfad: `/${t.id}` })}>
                    <Trash size={15} /></button>
                )}
                <button className="knopf leise" onClick={() => punktNeu.mutate(t.id)} disabled={punktNeu.isPending}>
                  <Plus size={15} weight="bold" /> Punkt
                </button>
              </>
            ) : (
              <h2>{String(t.nummer).padStart(2, "0")} {t.titel}</h2>
            )}
          </div>
          {t.eintraege.length === 0 && !gezogen && <div className="mute" style={{ padding: "10px 0" }}>keine Punkte</div>}
          {t.eintraege.map((e) => (
            <Ziehbar key={e.id} e={e} themaId={t.id} aktiv={offen}>
              {(griff) => (
                <Punkt e={e} offen={offen} beteiligte={beteiligte.data ?? []} projektId={p.projekt_id}
                       aendern={(r) => aendernMitRueck(e, r)}
                       entfernen={() => confirm(`Punkt ${e.nummer} entfernen?`) && punktWeg.mutate(e)}
                       alsMangel={p.meine_rolle !== "lesen" ? () => setMangelAus(e) : undefined}
                       ergaenzen={p.status === "freigegeben" && p.meine_rolle !== "lesen" ? () => setErgaenzung(e.id) : undefined}
                       ergaenzt={ergaenzt}
                       fotosGeaendert={neuLaden} griff={griff} />
              )}
            </Ziehbar>
          ))}
          {offen && <ThemaEnde themaId={t.id} sichtbar={!!gezogen} />}
        </section>
      ))}
      <DragOverlay dropAnimation={null}>
        {gezogen && (
          <div className="zieh-vorschau">
            <b>{gezogen.nummer}</b> {(gezogen.titel || gezogen.text || gezogen.verlauf.at(-1)?.text || "Punkt").slice(0, 90)}
          </div>
        )}
      </DragOverlay>
      </DndContext>

      {offen && (
        <form className="abschnitt-neu" onSubmit={(ev) => {
          ev.preventDefault();
          if (!neuerAbschnitt.trim()) return;
          gliederung.mutate({ methode: "POST", pfad: "", rumpf: { titel: neuerAbschnitt.trim() } },
                            { onSuccess: () => setNeuerAbschnitt("") });
        }}>
          <input className="eingabe" placeholder="Neuer Abschnitt, z. B. Vegetation" value={neuerAbschnitt}
                 onChange={(ev) => setNeuerAbschnitt(ev.target.value)} />
          <button className="knopf" disabled={!neuerAbschnitt.trim() || gliederung.isPending}><Plus size={15} /> Abschnitt</button>
          <div style={{ flex: 1 }} />
          <button type="button" className="knopf leise" onClick={() => setZurueck(true)}>
            <ArrowUUpLeft size={15} /> Erledigten Punkt zurückholen</button>
        </form>
      )}
      {rueck && (
        <div className="rueckgaengig" role="status">
          <span>{rueck.text}</span>
          <button className="knopf leise" onClick={() => { rueck.tun(); setRueck(null); }}>Rückgängig</button>
        </div>
      )}

      {offen && (
        <section className="karte" style={{ marginTop: 26 }}>
          <div className="raster">
            <label className="feld"><span>Nächste Besprechung</span>
              <input type="datetime-local" className="eingabe" defaultValue={ortszeitEingabe(p.naechste_am)}
                     onBlur={(e) => kopf.mutate({ naechste_am: e.target.value || null })} /></label>
            <label className="feld"><span>Ort</span><Blur wert={p.naechste_ort} speichern={(v) => kopf.mutate({ naechste_ort: v })} /></label>
          </div>
        </section>
      )}

      {p.status === "freigegeben" && (p.meine_rolle !== "lesen" || (p.ergaenzungen ?? []).length > 0) && (
        <section className="karte" style={{ marginTop: 26 }}>
          <div className="karte-kopf">
            <h2>Ergänzungen nach der Freigabe</h2>
            <span className="mute">z. B. aus Mails – werden in die nächste Besprechung übernommen</span>
            <div className="luft" />
            {p.meine_rolle !== "lesen" && <ErgaenzungKnopf onClick={() => setErgaenzung("")} />}
          </div>
          <ErgaenzungListe liste={p.ergaenzungen ?? []} kann={p.meine_rolle !== "lesen"} geaendert={ergaenzt} />
          {!(p.ergaenzungen ?? []).length && <div className="leer">Ergänzungen zu einzelnen Punkten stehen direkt am Punkt.</div>}
        </section>
      )}
      {ergaenzung !== null && <ErgaenzungDialog b={p} eintragId={ergaenzung} zu={() => setErgaenzung(null)} />}
      {p.status === "freigegeben" && <Einwaende b={p} beteiligte={beteiligte.data ?? []} />}

      <div className="leiste-unten">
        <a className="knopf" href={`/api/besprechungen/${p.id}/druck`} target="_blank" rel="noreferrer"><Eye size={16} /> Druckansicht</a>
        <a className="knopf" href={`/api/besprechungen/${p.id}/docx`} title="Arbeitsfassung im Büroformat – rechtsgültig ist das PDF">
          <FileDoc size={16} /> Word</a>
        {p.status === "entwurf"
          ? <a className="knopf" href={`/api/besprechungen/${p.id}/pdf`} target="_blank" rel="noreferrer"><FilePdf size={16} /> PDF (Entwurf)</a>
          : abgelegt
            ? <a className="knopf" href={`/api/schreiben/${abgelegt.id}/datei`} target="_blank" rel="noreferrer"><FilePdf size={16} /> PDF</a>
            : ausgang.isSuccess && p.meine_rolle === "leitung" &&
              <button className="knopf" disabled={nachholen.isPending} onClick={() => nachholen.mutate()}><FilePdf size={16} /> Ablage nachholen</button>}
        {stand && <span className={`gespeichert${stand.fehler ? " fehler" : ""}`}>{stand.text}</span>}
        <div className="luft" />
        {abgelegt && p.meine_rolle === "leitung" && (
          <button className={`knopf${abgelegt.verschickt ? "" : " haupt"}`} onClick={() => setVersand(true)}>
            <EnvelopeSimple size={16} /> {abgelegt.verschickt ? `Versendet an ${abgelegt.verschickt}` : "An Verteiler senden"}
          </button>
        )}
        {versand && abgelegt && <VersandDialog schreibenId={abgelegt.id} titel={abgelegt.titel} zu={() => setVersand(false)} />}
        {p.status === "entwurf" && p.meine_rolle === "leitung" && (
          <>
            {/* Ein wieder geöffnetes Protokoll ist schon verschickt: es wird korrigiert, nicht verworfen. */}
            {!p.wiedereroeffnungen?.length && (
              <button className="knopf leise gefahr" onClick={() => confirm("Diesen Entwurf verwerfen? Neue Punkte gehen verloren.") && verwerfen.mutate()}>
                <Trash size={16} /> Verwerfen
              </button>
            )}
            <button className="knopf haupt" onClick={() => setFreigeben(true)}>
              <CheckCircle size={16} /> {p.wiedereroeffnungen?.length ? `Als Fassung ${p.wiedereroeffnungen.length + 1} freigeben` : "Freigeben"}</button>
          </>
        )}
        {p.status === "freigegeben" && ich?.rolle === "admin" && (
          <button className="knopf leise" title="Nur Admins: das Protokoll wird wieder Entwurf, die nächste Freigabe ist Fassung 2"
                  onClick={() => setOeffnen(true)}><LockOpen size={16} /> Wieder öffnen</button>
        )}
      </div>
      <FreigabeDialog b={p} offen={freigeben} zu={() => setFreigeben(false)} fertig={neuLaden} />
      {oeffnen && <WiederoeffnenDialog b={p} zu={() => setOeffnen(false)} fertig={(neu) => {
        qc.setQueryData(schluessel, neu);
        for (const k of [["besprechungen", p.projekt_id], ["schreiben", p.projekt_id], ["projekte"]]) qc.invalidateQueries({ queryKey: k });
      }} />}
      {zurueck && <Zurueckholen besprechungId={p.id} zu={() => setZurueck(false)} fertig={() => { neuLaden(); gespeichert(); }} />}
      {mangelAus && (
        <NeuerMangel projekt={{ id: p.projekt_id }} leistungen={leistungen.data ?? []} offen zu={() => setMangelAus(null)}
                     punktId={mangelAus.punkt_id}
                     beschreibung={(mangelAus.text || mangelAus.verlauf.at(-1)?.text || mangelAus.titel).replace(/~~/g, "")} />
      )}
    </main>
  );
}

function Punkt({ e, offen, beteiligte, aendern, entfernen, alsMangel, ergaenzen, ergaenzt, projektId, fotosGeaendert, griff }: {
  e: Eintrag; offen: boolean; beteiligte: Beteiligter[]; projektId: string; fotosGeaendert: () => void;
  griff?: ReactNode; alsMangel?: () => void; ergaenzen?: () => void; ergaenzt: (b: Besprechung) => void;
  aendern: (r: Record<string, unknown>) => void; entfernen: () => void;
}) {
  const zu = e.status !== "offen";
  // Ein Tipp auf den schon gewaehlten Wert ist keine Aenderung: er setzte sonst
  // `uebernommen` zurueck (im Druck verschwaende „unveraendert“) und den Konfliktstand.
  const setze = (r: Record<string, unknown>) => {
    const d = Object.fromEntries(Object.entries(r).filter(([k, v]) => (e as unknown as Record<string, unknown>)[k] !== v));
    if (Object.keys(d).length) aendern(d);
  };
  return (
    <div className={`punkt${zu ? " geschlossen" : ""}${e.wichtig ? " wichtig" : ""}`}>
      <div className="punkt-nr">{griff}{e.nummer}</div>
      <div style={{ minWidth: 0 }}>
        {offen
          ? <Blur className="punkt-titel" wert={e.titel} placeholder="Gegenstand" speichern={(v) => aendern({ titel: v })} aria-label="Gegenstand" />
          : e.titel && <div className="punkt-titel">{e.titel}</div>}
        {e.verlauf.length > 0 && (
          <div className="verlauf">
            {e.verlauf.map((v, i) => (
              <div key={i}>
                <b>{v.nummer === null ? "aus früheren Protokollen" : `Nr. ${v.nummer} · ${datum(v.datum)}`}</b>{" "}
                <Durchgestrichen text={v.text} />
              </div>
            ))}
          </div>
        )}
        {offen
          ? <Blur mehrzeilig wert={e.text} placeholder={e.uebernommen ? "unverändert – oder neuen Stand eintragen" : "Inhalt"}
                  speichern={(v) => aendern({ text: v })} aria-label="Inhalt" />
          : <div className="punkt-text">{e.text ? <Durchgestrichen text={e.text} /> : (e.uebernommen ? <span className="mute">unverändert</span> : null)}</div>}
        <ErgaenzungListe liste={e.ergaenzungen ?? []} kann={!!ergaenzen} geaendert={ergaenzt} />
        <div className="punkt-leiste">
          {offen ? (
            <>
              <div className="wahl" role="group" aria-label="Art">
                {(["I", "F", "A"] as PunktArt[]).map((a) => (
                  <button key={a} title={PUNKT_ART[a]} className={e.art === a ? "an" : ""} onClick={() => setze({ art: a })}>{a}</button>
                ))}
              </div>
              <Auswahl value={e.zustaendig_id ?? ""} aria-label="Wer"
                      onChange={(x) => setze({ zustaendig_id: x.target.value || null })}>
                {/* Ohne Verweis steht nur die Abschrift da, etwa „Reese/Maschmann" aus Word (SPEC §4.6). */}
                <option value="">{e.zustaendig_id ? "Wer?" : e.zustaendig || "Wer?"}</option>
                {/* Solange die Beteiligten laden (oder der Beteiligte inzwischen geloescht
                    ist), steht die Abschrift da — sonst saehe ein zugewiesener Punkt leer aus. */}
                {e.zustaendig_id && !beteiligte.some((x) => x.id === e.zustaendig_id) &&
                  <option value={e.zustaendig_id}>{e.zustaendig || "…"}</option>}
                {beteiligte.map((b) => <option key={b.id} value={b.id}>{b.kuerzel || b.firma || b.name}</option>)}
              </Auswahl>
              {/* Erst beim Verlassen: beim Tippen der Jahreszahl entstehen sonst Zwischendaten
                  (0002, 0020 …), und jedes waere ein PATCH auf Eintrag und Punkt. */}
              <input key={e.frist ?? ""} type="date" className="eingabe" aria-label="Frist" defaultValue={e.frist ?? ""}
                     onBlur={(x) => setze({ frist: x.target.value || null })} />
              <div className="wahl" role="group" aria-label="Status">
                {(["offen", "erledigt", "entfallen"] as PunktStatus[]).map((s) => (
                  <button key={s} className={e.status === s ? "an" : ""} onClick={() => setze({ status: s })}>{s}</button>
                ))}
              </div>
              <div className="wahl" role="group" aria-label="Hervorhebung">
                <button title="Rot hervorheben – gilt nur in diesem Protokoll" className={`rot${e.wichtig ? " an" : ""}`}
                        onClick={() => setze({ wichtig: !e.wichtig })}>!</button>
                {/* Anders als „!" gilt das über die Sitzung hinaus (Wanderung 024). */}
                <button title={e.weiterfuehren ? "Bleibt in den Folgeprotokollen stehen, auch erledigt – zum Lösen klicken"
                                               : "In den Folgeprotokollen stehen lassen, auch wenn erledigt"}
                        aria-label="In Folgeprotokollen stehen lassen" aria-pressed={!!e.weiterfuehren}
                        className={e.weiterfuehren ? "an" : ""}
                        onClick={() => setze({ weiterfuehren: !e.weiterfuehren })}><PushPin size={14} weight={e.weiterfuehren ? "fill" : "regular"} /></button>
              </div>
              <Diktat text={e.text} speichern={(v) => aendern({ text: v })} />
              {e.neu && <button className="knopf leise symbol" aria-label="Punkt entfernen" onClick={entfernen}><Trash size={16} /></button>}
            </>
          ) : (
            <>
              <span className="chip">{PUNKT_ART[e.art]}</span>
              {e.zustaendig && <span className="chip akzent">{e.zustaendig}</span>}
              {e.frist && <span className="chip">Frist {datum(e.frist)}</span>}
              {zu && <span className="chip gut">{e.status}</span>}
              {e.wichtig && <span className="chip fehler">wichtig</span>}
              {e.weiterfuehren && <span className="chip" title="Bleibt in den Folgeprotokollen stehen"><PushPin size={12} /> bleibt stehen</span>}
            </>
          )}
          {e.maengel?.map((n) => (
            <Link key={n} className="chip warn" to={`/p/${projektId}/maengel`} title="Aus diesem Punkt erfasster Mangel">
              M-{String(n).padStart(3, "0")}</Link>
          ))}
          {ergaenzen && <ErgaenzungKnopf onClick={ergaenzen} />}
          {alsMangel && (
            <button className="knopf leise" title="Aus diesem Punkt einen Mangel erfassen" onClick={alsMangel}>
              <Warning size={15} /> Mangel</button>
          )}
        </div>
        {/* Fotos gehoeren zum EINTRAG, also zu dieser Sitzung (Wanderung 006), und frieren
            mit der Freigabe ein. */}
        <FotoLeiste projektId={projektId} ziel="eintrag_id" zielId={e.id} kann={offen}
                    fotos={e.fotos} geaendert={fotosGeaendert} />
      </div>
    </div>
  );
}

export const TEILNAHME: Record<Teilnahme["status"], string> = {
  anwesend: "anwesend", entschuldigt: "entschuldigt", abwesend: "nicht anwesend",
  verteiler: "Verteiler", vertretung: "nur Vertretung",
};

/** `~~…~~` = in Word durchgestrichen (übernommene Protokolle, SPEC §4.6). */
function Durchgestrichen({ text }: { text: string }) {
  const teile = text.split(/~~(.+?)~~/g);
  return <>{teile.map((t, i) => (i % 2 ? <s key={i}>{t}</s> : <span key={i}>{t}</span>))}</>;
}

function Teilnehmende({ b, offen, beteiligte, neuLaden, gemeldet }: {
  b: Besprechung; offen: boolean; beteiligte: Beteiligter[]; neuLaden: () => void; gemeldet: (f: unknown) => void;
}) {
  const [neu, setNeu] = useState("");
  const status = useMutation({
    scope: { id: protokollScope(b.id) },
    mutationFn: ({ id, s }: { id: string; s: string }) => aender(`/teilnahme/${id}`, { status: s }),
    onSuccess: neuLaden, onError: gemeldet,
  });
  const dazu = useMutation({
    scope: { id: protokollScope(b.id) },
    mutationFn: (bid: string) => schick(`/besprechungen/${b.id}/teilnahme`, { beteiligter_id: bid }),
    onSuccess: () => { setNeu(""); neuLaden(); }, onError: gemeldet,
  });
  const da = new Set(b.teilnahme.map((t) => t.beteiligter_id));
  const fehlen = beteiligte.filter((x) => !da.has(x.id));
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2>Teilnehmende und Verteiler</h2>
        <span className="mute">{b.teilnahme.filter((t) => t.status === "anwesend").length} anwesend</span>
      </div>
      <div className="liste">
        {b.teilnahme.map((t) => (
          <div key={t.id} className="zeile" style={{ minHeight: 48, padding: "6px 4px" }}>
            <span className="nr">{t.kuerzel}</span>
            <div className="haupt"><div>{t.name || t.firma}</div>
              <div className="mute">{[t.name && t.firma, t.email].filter(Boolean).join(" · ")}</div></div>
            {offen ? (
              <Auswahl style={{ width: "auto" }} value={t.status}
                      onChange={(e) => status.mutate({ id: t.id, s: e.target.value })}>
                {Object.entries(TEILNAHME).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Auswahl>
            ) : <span className="chip">{TEILNAHME[t.status]}</span>}
          </div>
        ))}
      </div>
      {offen && fehlen.length > 0 && (
        <div className="punkt-leiste">
          <Auswahl value={neu} onChange={(e) => setNeu(e.target.value)}>
            <option value="">Beteiligten hinzufügen …</option>
            {fehlen.map((x) => <option key={x.id} value={x.id}>{[x.firma, x.name].filter(Boolean).join(" · ")}</option>)}
          </Auswahl>
          <button className="knopf" disabled={!neu} onClick={() => dazu.mutate(neu)}><UserPlus size={16} /> hinzufügen</button>
        </div>
      )}
    </section>
  );
}

function FreigabeDialog({ b, offen, zu, fertig }: { b: Besprechung; offen: boolean; zu: () => void; fertig: () => void }) {
  const qc = useQueryClient();
  const [hinweis, setHinweis] = useState("");
  // Im Scope des Protokolls: Aenderungen, die beim Klick noch in der Schlange stehen,
  // laufen VOR der Freigabe durch — sonst fehlten die letzten Worte im eingefrorenen Stand.
  const los = useMutation({
    scope: { id: protokollScope(b.id) },
    mutationFn: () => schick<{ ok: boolean; schreiben: unknown; hinweis?: string }>(`/besprechungen/${b.id}/freigabe`),
    onSuccess: (r) => {
      fertig();
      for (const k of [["besprechungen", b.projekt_id], ["schreiben", b.projekt_id], ["projekte"]])
        qc.invalidateQueries({ queryKey: k });
      // Freigegeben ist es so oder so; fehlt die Ablage, sagen wir es, statt still zu schliessen.
      if (r.schreiben) zu();
      else setHinweis(r.hinweis || "Freigegeben, aber das PDF liegt noch nicht im Ausgang. Unten mit „Ablage nachholen“ erneut versuchen.");
    },
  });
  const offenePunkte = b.themen.flatMap((t) => t.eintraege).filter((e) => e.status === "offen").length;
  return (
    <Dialog offen={offen} zu={zu} titel={`Protokoll Nr. ${b.nummer} freigeben`} fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={los.isPending} onClick={() => los.mutate()}>Freigeben</button>
    </>}>
      <p style={{ marginTop: 0 }}>Nach der Freigabe lässt sich das Protokoll nicht mehr ändern. Das PDF wird
        mit Prüfsumme im Ausgang abgelegt; Korrekturen gehören als Punkt in die nächste Besprechung.</p>
      <p className="mute">{offenePunkte} offene Punkte wandern in die nächste Besprechung.</p>
      <Meldung fehler={los.error} />
      {hinweis && <div className="meldung">{hinweis}</div>}
    </Dialog>
  );
}

/** Ablageziel „vor diesen Punkt" bzw. „ans Ende des Themas". Erst der Zeiger (genau), dann
 *  die Ueberlappung: mit der Tastatur gibt es keinen Zeigerpunkt. */
const zielErkennen: CollisionDetection = (args) => {
  const treffer = pointerWithin(args);
  return treffer.length ? treffer : rectIntersection(args);
};

/** Ein Punkt ist zugleich Zugstueck (am Griff) und Ablageziel (davor einfuegen). */
function Ziehbar({ e, themaId, aktiv, children }: {
  e: Eintrag; themaId: string; aktiv: boolean; children: (griff: ReactNode) => ReactNode;
}) {
  const zug = useDraggable({ id: e.id, data: { e }, disabled: !aktiv });
  const ablage = useDroppable({ id: `vor:${e.id}`, data: { thema_id: themaId, vor: e.id }, disabled: !aktiv });
  const griff = aktiv ? (
    <button type="button" className="ziehgriff" aria-label={`Punkt ${e.nummer} verschieben`}
            title="Ziehen, um den Punkt zu verschieben" {...zug.listeners} {...zug.attributes}>
      <DotsSixVertical size={18} weight="bold" />
    </button>
  ) : null;
  return (
    <div ref={(n) => { zug.setNodeRef(n); ablage.setNodeRef(n); }}
         className={`ziehbar${zug.isDragging ? " zieht" : ""}${ablage.isOver && !zug.isDragging ? " davor" : ""}`}>
      {children(griff)}
    </div>
  );
}

function ThemaEnde({ themaId, sichtbar }: { themaId: string; sichtbar: boolean }) {
  const ablage = useDroppable({ id: `ende:${themaId}`, data: { thema_id: themaId, vor: null } });
  return (
    <div ref={ablage.setNodeRef} className={`thema-ende${sichtbar ? " sichtbar" : ""}${ablage.isOver ? " davor" : ""}`}>
      {sichtbar && "hierher ziehen – ans Ende dieses Abschnitts"}
    </div>
  );
}

/** Dieselbe Umordnung wie im Backend (protokoll.verschieben), nur fuer die Anzeige bis
 *  zur Antwort. Die Nummern bleiben bis dahin die alten. */
function umsortiert(b: Besprechung, e: Eintrag, themaId: string, vor: string | null): Besprechung {
  const ohne = b.themen.map((t) => ({ ...t, eintraege: t.eintraege.filter((x) => x.id !== e.id) }));
  return {
    ...b,
    themen: ohne.map((t) => {
      if (t.id !== themaId) return t;
      const liste = [...t.eintraege];
      const i = vor ? liste.findIndex((x) => x.id === vor) : -1;
      liste.splice(i < 0 ? liste.length : i, 0, e);
      return { ...t, eintraege: liste };
    }),
  };
}

/** Nur Admins (SPEC §4.14): ein freigegebenes Protokoll wieder öffnen — mit Grund, der
 *  im Journal und im Kopf der nächsten Fassung steht. */
function WiederoeffnenDialog({ b, zu, fertig }: { b: Besprechung; zu: () => void; fertig: (neu: Besprechung) => void }) {
  const [grund, setGrund] = useState("");
  const los = useMutation({
    mutationFn: () => schick<Besprechung>(`/besprechungen/${b.id}/wiedereroeffnen`, { grund }),
    onSuccess: (neu) => { fertig(neu); zu(); },
  });
  return (
    <Dialog offen zu={zu} titel={`Protokoll Nr. ${b.nummer} wieder öffnen`} fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={grund.trim().length < 3 || los.isPending} onClick={() => los.mutate()}>
        <LockOpen size={16} /> Wieder öffnen</button>
    </>}>
      <p style={{ marginTop: 0 }}>Das Protokoll wird wieder Entwurf und lässt sich korrigieren. Danach erneut freigeben:</p>
      <ul className="mute" style={{ marginTop: 0 }}>
        <li>Es wird <b>Fassung {(b.fassung ?? 1) + 1}</b> mit neuem PDF; im Kopf steht, dass sie Fassung {b.fassung ?? 1} ersetzt, und der Grund.</li>
        <li>Das bisherige PDF bleibt im Ausgang – es ist das, was verschickt wurde. Die neue Fassung bitte wieder an den Verteiler senden.</li>
        <li>Bis zur erneuten Freigabe sehen Lesende das Protokoll nicht.</li>
        <li>Geht nur beim jüngsten Protokoll und solange es keinen anderen Entwurf gibt.</li>
      </ul>
      <label className="feld"><span>Grund (Pflicht)</span>
        <textarea className="eingabe" rows={3} autoFocus value={grund} placeholder="z. B. Frist in Punkt 03.02 falsch übernommen"
                  onChange={(e) => setGrund(e.target.value)} /></label>
      <Meldung fehler={los.error} />
    </Dialog>
  );
}
